import { forwardRef, useEffect, useImperativeHandle, useRef, useCallback } from "react";
import { Control, FieldPath, FieldValues, useController } from "react-hook-form";
import { useDocumentManagement, DocumentItem } from "./hooks/useDocumentManagement";
import { useDocumentUpload } from "./hooks/useDocumentUpload";
import { DocumentHeader } from "./DocumentHeader";
import { DocumentList } from "./DocumentList";
import { createNewDocument } from "./utils/documentUtils";

interface ImpactResultDocumentsProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  control: Control<TFieldValues>;
  dataView?: boolean;
  documents: DocumentItem[];
  onDocumentsChange: (documents: DocumentItem[]) => void;
  ingredientId: string;
  supplierId: string;
}

export interface ImpactResultDocumentsRef {
  getDocuments(): DocumentItem[];
  getUploadedDocuments(): any[];
}

export const ImpactResultDocuments = forwardRef<
  ImpactResultDocumentsRef,
  ImpactResultDocumentsProps
>(
  (
    {
      control,
      dataView = false,
      documents,
      onDocumentsChange,
      ingredientId,
      supplierId
    }: ImpactResultDocumentsProps,
    ref
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { documents: uploadedDocuments, ...documentOps } = useDocumentManagement(documents);

    // Use controller to manage documents in form
    const { field } = useController({
      control,
      name: "supportingDocuments_notes" as any,
      defaultValue: {}
    });

    const { uploadMultipleDocuments } = useDocumentUpload({
      ingredientId,
      supplierId,
      onDocumentUpdate: (id, updates) => {
        documentOps.updateDocument(id, updates);
      }
    });

    // Sync documents prop with internal state
    useEffect(() => {
      documentOps.setAllDocuments(documents);
    }, [documents]);

    // Notify parent when documents change
    useEffect(() => {
      onDocumentsChange(uploadedDocuments);
    }, [uploadedDocuments]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      getDocuments: () => uploadedDocuments,
      getUploadedDocuments: () => {
        return uploadedDocuments
          .filter(doc => !doc.uploadError) // Exclude documents with errors
          .map(doc => {
            // If document already has uploadedResponse, use it
            if (doc.uploadedResponse) {
              return {
                ...doc.uploadedResponse,
                note: doc.note
              };
            }
            // For queued documents (new registration), return file object
            return {
              file: doc.file,
              note: doc.note,
              description: doc.description || "",
              fileName: doc.file.name,
              fileSize: doc.file.size
            };
          });
      }
    }));

    const handleUploadClick = () => {
      fileInputRef.current?.click();
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;

      const newDocs = Array.from(files).map(createNewDocument);
      documentOps.addDocuments(newDocs);

      // If we have supplierId, upload immediately
      if (ingredientId && supplierId) {
        await uploadMultipleDocuments(newDocs);
      }
      // Otherwise, files are queued for upload during save (for new supplier registration)

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    const handleNoteChange = useCallback((id: string, note: string) => {
      documentOps.updateDocumentNote(id, note);
      // Update form field value
      field.onChange({
        ...field.value,
        [id]: note
      });
    }, [documentOps, field]);

    return (
      <div className="tw-rounded-lg tw-border tw-border-border tw-p-6">
        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="tw-hidden"
          accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.xls,.xlsx"
        />

        {/* Header Section */}
        <DocumentHeader
          dataView={dataView}
          onUploadClick={handleUploadClick}
          isDisabled={!supplierId}
        />

        {/* Message when supplier not yet created (new supplier registration) */}
        {!supplierId && uploadedDocuments.length === 0 && (
          <div className="tw-mb-4 tw-p-3 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-md">
            <p className="tw-text-sm tw-text-blue-800">
              💡 <strong>Tip:</strong> Save the supplier first to enable document upload. Documents will be associated with the created supplier.
            </p>
          </div>
        )}

        {/* Documents List Section */}
        <DocumentList
          documents={uploadedDocuments}
          dataView={dataView}
          onNoteChange={handleNoteChange}
          onDelete={documentOps.deleteDocument}
        />
      </div>
    );
  }
);

ImpactResultDocuments.displayName = "ImpactResultDocuments";
